import type { ExitType } from '@/lib/exitTypes'
import { apiClient } from '@/api/client'
import {
  COMPANIES,
  COMPANY_MEMBERS,
  ME_COMPANIES,
  companyByIdPath,
  companyDashboardPath,
  companyMembersPath,
  companyMemberPath,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type { PagedListBaseParams } from '@/hooks/usePagedListParams'
import type {
  Company,
  CompanyDashboard,
  CreateCompanyPayload,
  UpdateCompanyPayload,
  CompanyMember,
  AddCompanyMemberPayload,
  UpdateCompanyMemberPayload,
  MeCompany,
} from '@/types/company.types'

function withoutLegacySlug<T extends object>(payload: T): Omit<T, 'slug'> {
  const next = { ...payload } as T & { slug?: unknown }
  delete next.slug
  return next
}

export async function createCompany(payload: CreateCompanyPayload): Promise<Company> {
  const { data } = await apiClient.post<ApiResponseWrapper<Company>>(
    COMPANIES,
    withoutLegacySlug(payload)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to create company')
}

export interface GetCompaniesParams {
  memberOf?: 'me'
  search?: string
}

export async function getCompanies(params?: GetCompaniesParams): Promise<Company[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<Company[]>>(COMPANIES, { params })
  const list = data?.data
  if (Array.isArray(list)) return list
  return []
}

export async function getCompanyById(id: string): Promise<Company> {
  const { data } = await apiClient.get<ApiResponseWrapper<Company>>(companyByIdPath(id))
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Company not found')
}

export async function getCompanyDashboard(id: string): Promise<CompanyDashboard> {
  const { data } = await apiClient.get<ApiResponseWrapper<CompanyDashboard>>(
    companyDashboardPath(id)
  )
  if (data?.data && typeof data.data === 'object') {
    return data.data
  }
  throw new Error('Failed to load company dashboard')
}

export async function updateCompany(id: string, payload: UpdateCompanyPayload): Promise<Company> {
  const { data } = await apiClient.patch<ApiResponseWrapper<Company>>(
    companyByIdPath(id),
    withoutLegacySlug(payload)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to update company')
}

export async function deleteCompany(id: string): Promise<void> {
  await apiClient.delete(companyByIdPath(id))
}

export async function getMyCompanies(): Promise<MeCompany[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<MeCompany[]>>(ME_COMPANIES)
  const list = data?.data
  if (Array.isArray(list)) return list
  return []
}

export type CompanyMembersListParams = PagedListBaseParams

export interface MembersWorkspaceQuery extends PagedListBaseParams {
  companyIds?: string[]
  status?: 'active' | 'inactive'
  compensationType?: 'salaried' | 'hourly'
  /** Matches member name, email, employee ID, or company name (case-insensitive). */
  search?: string
}

export async function getMembersWorkspace(
  query?: MembersWorkspaceQuery
): Promise<PaginatedResponse<CompanyMember>> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: CompanyMember[]
      pagination: { total: number; page: number; limit: number }
    }>
  >(COMPANY_MEMBERS, { params })
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) {
    const { total, page, limit } = payload.pagination
    return {
      data: payload.data,
      total,
      page,
      limit,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    }
  }
  return {
    data: [],
    total: 0,
    page: 1,
    limit: query?.limit ?? 10,
    totalPages: 1,
  }
}

export async function getCompanyMembers(
  companyId: string,
  params: CompanyMembersListParams
): Promise<PaginatedResponse<CompanyMember>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<CompanyMember>>>(
    companyMembersPath(companyId),
    { params }
  )
  return data.data
}

export async function addCompanyMember(
  companyId: string,
  payload: AddCompanyMemberPayload
): Promise<CompanyMember> {
  const { data } = await apiClient.post<ApiResponseWrapper<CompanyMember>>(
    companyMembersPath(companyId),
    payload
  )
  if (data?.data && typeof data.data === 'object') {
    return data.data
  }
  throw new Error('Failed to add company member')
}

export async function updateCompanyMember(
  companyId: string,
  userId: string,
  payload: UpdateCompanyMemberPayload
): Promise<CompanyMember> {
  const { data } = await apiClient.patch<ApiResponseWrapper<CompanyMember>>(
    companyMemberPath(companyId, userId),
    payload
  )
  if (data?.data && typeof data.data === 'object') {
    return data.data
  }
  throw new Error('Failed to update company member')
}

/** Removal ends the member's employment in the company, so it says why. */
export async function removeCompanyMember(
  companyId: string,
  userId: string,
  exit: { exitType: ExitType; reason?: string }
): Promise<void> {
  await apiClient.delete(companyMemberPath(companyId, userId), { data: exit })
}
